import { type CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { Award, Info, MousePointerClick, Type } from 'lucide-react';
import { toast } from 'sonner';
import { EmbeddedBlock } from '@tinct/core-api-models';
import { isBlockPersonalizationEnabled } from '../../../../../utils/is-block-personalization-enabled';

// Rendered inside the page iframe: no app stylesheet, so everything is inline
// and hard-coded to the dark palette (the badge is dark in both themes).
const FONT_FAMILY = 'system-ui, sans-serif';
const SURFACE = '#262626';
const FOREGROUND = '#fafafa';
const MUTED_FOREGROUND = '#a3a3a3';
const PRIMARY = '#6366f1';
const DISABLED = '#a3a3a3';

const EVENT_NAME_HINT =
  'Name of the click event sent to your analytics when a visitor clicks this element.';

const labelStyle: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  lineHeight: 1,
  color: FOREGROUND,
  whiteSpace: 'nowrap',
};

const separatorStyle: CSSProperties = {
  width: 1,
  height: 16,
  flexShrink: 0,
  background: 'rgba(255,255,255,0.1)',
};

function blockTypeChip(block: EmbeddedBlock) {
  switch (block.type) {
    case 'cta':
      return { color: '#14b8a6', Icon: MousePointerClick };
    case 'customer_logo':
      return { color: '#f59e0b', Icon: Award };
    default:
      return { color: PRIMARY, Icon: Type };
  }
}

type InlineSwitchProps = {
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
};

/** Label + 28×16 switch, styled after the app's small Switch. */
function InlineSwitch({ label, checked, onCheckedChange }: InlineSwitchProps) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
      <span style={labelStyle}>{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onCheckedChange(!checked)}
        style={{
          boxSizing: 'border-box',
          display: 'inline-flex',
          alignItems: 'center',
          width: 28,
          height: 16,
          margin: 0,
          padding: '0 2px',
          border: '2px solid transparent',
          borderRadius: 9999,
          background: checked ? PRIMARY : 'rgba(255,255,255,0.25)',
          cursor: 'pointer',
          outline: 'none',
          appearance: 'none',
          flexShrink: 0,
          transition: 'background 0.15s ease',
        }}
      >
        <span
          style={{
            display: 'block',
            width: 12,
            height: 12,
            borderRadius: 9999,
            background: '#fff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 1px 2px -1px rgba(0,0,0,0.1)',
            transform: checked ? 'translateX(8px)' : 'translateX(0)',
            transition: 'transform 0.15s ease',
          }}
        />
      </button>
    </label>
  );
}

type EventNameBoxProps = {
  block: EmbeddedBlock;
  onBlockUpdate: (block: EmbeddedBlock) => void;
};

/**
 * Event-name field for a tracked CTA, floating under the badge. Saves after a
 * typing pause or on blur (same policy as the former sidebar section).
 */
function EventNameBox({ block, onBlockUpdate }: EventNameBoxProps) {
  const [localEventName, setLocalEventName] = useState(block.eventName ?? '');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [prevBlockId, setPrevBlockId] = useState(block.id);

  // Reset local state when switching to a different block
  if (block.id !== prevBlockId) {
    setPrevBlockId(block.id);
    setLocalEventName(block.eventName ?? '');
  }

  const flushEventName = useCallback(
    (value: string) => {
      if (value === (block.eventName ?? '')) return;
      onBlockUpdate({ ...block, eventName: value });
    },
    [block, onBlockUpdate]
  );

  const handleChange = useCallback(
    (value: string) => {
      setLocalEventName(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => flushEventName(value), 3000);
    },
    [flushEventName]
  );

  const handleBlur = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    flushEventName(localEventName);
  }, [flushEventName, localEventName]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <div
      style={{
        position: 'absolute',
        top: 'calc(100% + 8px)',
        left: -8,
        boxSizing: 'border-box',
        width: 208,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        padding: 8,
        borderRadius: 8,
        background: SURFACE,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 1px 2px -1px rgba(0,0,0,0.1)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 18 }}>
        <span style={{ ...labelStyle, color: MUTED_FOREGROUND, lineHeight: '18px' }}>Event name</span>
        <span title={EVENT_NAME_HINT} style={{ display: 'inline-flex', color: MUTED_FOREGROUND, cursor: 'help' }}>
          <Info size={16} strokeWidth={1.5} aria-label={EVENT_NAME_HINT} />
        </span>
      </div>
      <input
        type="text"
        value={localEventName}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={handleBlur}
        placeholder="e.g. hero_cta_clicked"
        aria-label="Event name"
        style={{
          boxSizing: 'border-box',
          width: '100%',
          height: 36,
          margin: 0,
          padding: '4px 12px',
          border: '1px solid rgba(255,255,255,0.15)',
          borderRadius: 8,
          background: 'rgba(255,255,255,0.05)',
          color: FOREGROUND,
          fontFamily: FONT_FAMILY,
          fontSize: 14,
          fontWeight: 400,
          lineHeight: '20px',
          outline: 'none',
          appearance: 'none',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        }}
      />
    </div>
  );
}

export type BlockBadgesProps = {
  block: EmbeddedBlock;
  /**
   * Present only when the badge is editable (studio original tab): adds the
   * AI-personalization switch and, for CTAs, tracking + event name.
   */
  onBlockUpdate?: (block: EmbeddedBlock) => void;
};

export function BlockBadges({ block, onBlockUpdate }: BlockBadgesProps) {
  const enabled = isBlockPersonalizationEnabled(block);
  const { color, Icon } = blockTypeChip(block);
  const editable = onBlockUpdate !== undefined;
  const isCta = block.type === 'cta';

  const handleEnabledChange = (checked: boolean) => {
    onBlockUpdate?.({ ...block, enabled: checked });
    toast.success(
      checked
        ? `AI personalization enabled for ${block.label || block.id}`
        : `AI personalization disabled for ${block.label || block.id}`,
      { position: 'bottom-center' }
    );
  };

  const handleTrackChange = (checked: boolean) => {
    onBlockUpdate?.({ ...block, track: checked });
    toast.success(
      checked
        ? `Tracking enabled for ${block.label || block.id}`
        : `Tracking disabled for ${block.label || block.id}`,
      { position: 'bottom-center' }
    );
  };

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        boxSizing: 'border-box',
        height: 24,
        padding: 4,
        borderRadius: 8,
        background: SURFACE,
        color: FOREGROUND,
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
        // Neutralize whatever the host page sets on inherited text props.
        fontFamily: FONT_FAMILY,
        letterSpacing: 'normal',
        textTransform: 'none',
        textAlign: 'left',
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <span
          aria-hidden="true"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 16,
            height: 16,
            borderRadius: 4,
            background: enabled ? color : DISABLED,
            color: '#fff',
            flexShrink: 0,
          }}
        >
          <Icon size={12} strokeWidth={2} />
        </span>
        <span style={labelStyle}>{block.label ?? 'Block'}</span>
      </span>

      {editable && (
        <>
          <span style={separatorStyle} />
          <InlineSwitch
            label="AI Personalization"
            checked={enabled}
            onCheckedChange={handleEnabledChange}
          />
        </>
      )}

      {editable && isCta && (
        <>
          <span style={separatorStyle} />
          <span style={{ position: 'relative', display: 'inline-flex' }}>
            <InlineSwitch
              label="Tracking"
              checked={block.track ?? false}
              onCheckedChange={handleTrackChange}
            />
            {block.track && <EventNameBox block={block} onBlockUpdate={onBlockUpdate} />}
          </span>
        </>
      )}
    </div>
  );
}
